import React, { KeyboardEvent, MouseEvent, useEffect, useState } from 'react';
import {
	getNextFocusableElement,
	getPrevFocusableElement,
} from '../../../../../../_helpers/dom';
import { getKeys } from '../../../../../../_helpers/keyboard';
import { HeaderUserTypeData } from '../../../../../../components/blackrock/advisor-center/_types/header';

import './HeaderUserTypeDesktop.scss';

const WINDOW_RESIZE_DEBOUNCE_DELAY = 500;

export type HeaderUserTypeDesktopProps = {
	isUserTypeOpened: boolean;
	isUserTypeVisible: boolean;
	userType: HeaderUserTypeData;
	userTypeClose: () => void;
	userTypeHide: () => void;
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const HeaderUserTypeDesktop: React.FC<HeaderUserTypeDesktopProps> = ({
	userTypeHide,
	userTypeClose,
	isUserTypeVisible,
	isUserTypeOpened,
	userType,
}) => {
	const [positionRight, setPositionRight] = useState('200px');

	let titleElement: HTMLElement;

	let resizeTimoutID = 0;

	let el: HTMLElement;

	const userTypeData = userType;

	const isTurnedOn =
		!!userTypeData.mainHeader ||
		!!userTypeData.locationDescription ||
		!!userTypeData.userTypeDescription;

	const isActive = isTurnedOn && isUserTypeVisible;

	useEffect(() => {
		const mastHeadUserTypeSelector =
			document.getElementById('userTypeSelector');

		if (mastHeadUserTypeSelector) {
			mastHeadUserTypeSelector.addEventListener('click', () => {
				userTypeClose();
			});
		}

		setBackgroundPosition();

		el?.addEventListener('transitionend', userTypeHide);

		window.addEventListener('resize', onWindowResize);

		return () => {
			el?.removeEventListener('transitionend', userTypeHide);
			window.removeEventListener('resize', onWindowResize);
		};
	}, []);

	const onWindowResize = () => {
		window.clearTimeout(resizeTimoutID);
		resizeTimoutID = window.setTimeout(
			() => setBackgroundPosition(),
			WINDOW_RESIZE_DEBOUNCE_DELAY
		);
	};

	const setBackgroundPosition = (): void => {
		const selectorElement = document.querySelector(
			'#userTypeSelector'
		) as HTMLElement;

		if (!el) {
			return;
		}

		const pagePadding = (document.body.offsetWidth - el.offsetWidth) / 2;

		setPositionRight('200px');

		if (selectorElement) {
			const selectorWidth = selectorElement.offsetWidth;
			const selectorPosition = selectorElement.getBoundingClientRect().x;
			const targetPosition = selectorPosition + selectorWidth / 2 - pagePadding;

			setPositionRight(el.offsetWidth - targetPosition - 6 + 'px');
		}
	};

	const onCloseButtonClicked = (event: MouseEvent) => {
		event.stopPropagation();
		userTypeClose();
	};

	const onKeyDownOfContainer = (event: KeyboardEvent): void => {
		const keys = getKeys(event.nativeEvent);

		if (keys.isEnter || keys.isDown || keys.isSpace) {
			event.preventDefault();
			titleElement.focus();
		}
	};

	const onKeyDownOfElements = (event: KeyboardEvent): void => {
		const keys = getKeys(event.nativeEvent);
		const target = event.target as HTMLElement;

		event.stopPropagation();

		if (keys.isDown) {
			event.preventDefault();
			const nextFocusableElement = getNextFocusableElement(target, el);
			nextFocusableElement?.focus();
		}

		if (keys.isUp) {
			event.preventDefault();
			const prevFocusableElement = getPrevFocusableElement(target, el);
			prevFocusableElement?.focus();
		}

		if (keys.isEscape) {
			event.stopPropagation();
			el.focus();
		}
	};

	const onClick = (event: MouseEvent): void => {
		const target = event.target as HTMLElement;

		if (target.tagName === 'A') {
			userTypeClose();
		}
	};

	return (
		isActive && (
			<div
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				ref={(e) => (el = e)}
				onKeyDown={(e) => onKeyDownOfContainer(e)}
				aria-label="User type selector"
				className={`blk-ac-header-user-type-desktop ${
					isUserTypeOpened ? 'is-opened' : ''
				}`}
				style={
					{
						'--bg-position-right': positionRight,
					} as React.CSSProperties
				}
			>
				<div className="icon-container">
					<i className="icon-bell fa fa-bell" />
				</div>
				<div className="message-container">
					<h3
						className="message-title blk-ac-body-m-bold"
						// eslint-disable-next-line @typescript-eslint/ban-ts-comment
						// @ts-ignore
						ref={(ref) => (titleElement = ref)}
						onKeyDown={(e) => onKeyDownOfElements(e)}
					>
						{userTypeData.mainHeader}
					</h3>
					{userTypeData.locationDescription && (
						<p
							className="message-content blk-ac-body-xs-compact"
							dangerouslySetInnerHTML={{
								__html: userTypeData.locationDescription,
							}}
							onKeyDown={(e) => onKeyDownOfElements(e)}
							onClick={(e) => onClick(e)}
						></p>
					)}
					{userTypeData.userTypeDescription && (
						<p
							className="message-content blk-ac-body-xs-compact"
							dangerouslySetInnerHTML={{
								__html: userTypeData.userTypeDescription,
							}}
							onKeyDown={(e) => onKeyDownOfElements(e)}
							onClick={(e) => onClick(e)}
						></p>
					)}
				</div>
				<div className="action-container">
					<button
						title="Close user type selector"
						aria-label="Close user type selector"
						className="action-close"
						onClick={(e) => onCloseButtonClicked(e)}
						onKeyDown={(e) => onKeyDownOfElements(e)}
						aria-hidden="false"
					>
						<i className="fa fa-times"></i>
					</button>
				</div>
				<img
					src="https://www.blackrock.com/blk-one-c-assets/cache-1677729674000/include/one/css/blk/i/vi20/BLK_icn_user_type_noti.svg"
					className="decoration-background"
					alt=""
					data-final-url
				/>
			</div>
		)
	);
};

export default HeaderUserTypeDesktop;
